function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { Container, Row, Col, Table, Button, ButtonGroup } from 'reactstrap';
import PickerRow from './pickerRow';
import Icon from '../../common/containers/icon';
import SectionPanel from '../../common/containers/sectionPanel';
import entityComposition from '../../common/classes/entityComposition';
import routeHelper from '../../common/classes/routeHelper';
import { hasPermission } from '../../../utils/paths';
const postNameDuplicationWarning = notificationActions => notificationActions.error({
  message: 'Could not update, please ensure that all names are distinct.'
});
const postInvalidNameWarning = notificationActions => notificationActions.error({
  message: 'Could not update, please enter a valid picker name.'
});
const postPendingChangesWarning = notificationActions => notificationActions.error({
  message: 'Please finish editing picker values before attempting to save.'
});

// must be defined outside of the component to avoid re-rendering loops
const defaultLocationFunction = () => {};
const Picker = ({
  picker: {
    list,
    indexQuery
  },
  location: _location = defaultLocationFunction,
  canView,
  notificationActions,
  showDisabled,
  lang,
  onHasChange,
  hasChange,
  pickerActions: {
    serverIndex,
    serverSaveAll
  },
  pickerType
}) => {
  const [pickerRows, setPickerRows] = useState([]);
  // const [hasChange, setHasChange] = useState(false);
  const [isEditingHashMap, setIsEditingHashMap] = useState({});
  // Effect queries the server for the newest
  // version of the csv list
  useEffect(() => {
    routeHelper.handleViewRedirect(canView, _location);
    setPickerRows([]);
    serverIndex(_objectSpread(_objectSpread({}, indexQuery), {}, {
      table: pickerType.tableName,
      lang,
      filters: {
        disabled: showDisabled
      }
    }));
  }, [indexQuery, pickerType, serverIndex, lang, showDisabled, canView, _location]);
  // Effect firest when we recieve a list and we don't have one
  // already tracked by state
  useEffect(() => {
    if (pickerRows.length === 0 && list.length > 0) {
      setPickerRows(list.sort((l, r) => l.sortOrder - r.sortOrder));
    }
  }, [list, pickerRows]);
  const updateList = modifiedList => {
    onHasChange(true);
    const result = modifiedList;
    setPickerRows(result);
  };
  const onSave = () => {
    const isCurrentlyEditing = Object.values(isEditingHashMap).some(editing => editing);
    if (isCurrentlyEditing) {
      postPendingChangesWarning(notificationActions);
      return;
    }
    serverSaveAll(pickerType.tableName, lang, showDisabled, pickerRows);
    onHasChange(false);
    setPickerRows([]);
  };
  const onEditingChange = (index, isEditing) => {
    const mapCopy = JSON.parse(JSON.stringify(isEditingHashMap));
    mapCopy[index] = isEditing;
    setIsEditingHashMap(mapCopy);
  };
  const onAdd = () => {
    const newID = uuid();
    const newPickerItem = {
      id: newID,
      name: '',
      disabled: showDisabled,
      identifier: newID,
      metaDeleted: 0,
      sortOrder: pickerRows.length > 0 ? Math.max(...pickerRows.map(row => row.sortOrder)) + 1 : 0
    };
    const newList = pickerRows.concat([newPickerItem]);
    updateList(newList);
    onEditingChange(newList.length - 1, true);
  };
  const onTextChange = (updatedText, index) => {
    const listCopy = JSON.parse(JSON.stringify(pickerRows));
    const foundIndex = listCopy.findIndex(item => item.name === updatedText);
    if (updatedText === '') {
      postInvalidNameWarning(notificationActions);
    } else if (foundIndex !== -1 && foundIndex !== index) {
      postNameDuplicationWarning(notificationActions);
    } else {
      listCopy[index].name = updatedText;
      updateList(listCopy);
      onEditingChange(index, false);
    }
  };
  const changeOrder = (prev, curr) => {
    const listCopy = JSON.parse(JSON.stringify(pickerRows));
    const [item] = listCopy.splice(prev, 1);
    item.hasChange = true;
    listCopy.splice(curr, 0, item);
    updateList(listCopy);
  };
  const onDisableToggle = (isDisabled, index) => {
    const listCopy = JSON.parse(JSON.stringify(pickerRows));
    listCopy[index].disabled = isDisabled ? 1 : 0;
    updateList(listCopy);
  };
  return /*#__PURE__*/React.createElement(Container, {
    fluid: true
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: 12
  }, /*#__PURE__*/React.createElement(SectionPanel, {
    isPending: hasChange,
    isSaveDisabled: !hasChange,
    name: pickerType.name,
    onBack: false,
    onSave: onSave,
    showSave: true
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: 12
  }, /*#__PURE__*/React.createElement(Table, null, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
    style: {
      borderTop: 0
    }
  }, "Title"), /*#__PURE__*/React.createElement("th", {
    style: {
      borderTop: 0
    }
  }, /*#__PURE__*/React.createElement(ButtonGroup, {
    className: "float-md-right"
  }, /*#__PURE__*/React.createElement(Button, {
    className: "t-button-new",
    size: "sm",
    onClick: onAdd
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "plus"
  }), " New"))))), /*#__PURE__*/React.createElement("tbody", null, pickerRows.map((row, index) => /*#__PURE__*/React.createElement(PickerRow, {
    id: row.id,
    index: index,
    onDisableToggle: isDisabled => onDisableToggle(isDisabled, index),
    key: row.id,
    name: row.name,
    disabled: row.disabled,
    editing: isEditingHashMap[index] || false,
    sortOrder: row.sortOrder,
    onEditingStart: () => onEditingChange(index, true),
    onTextChange: updatedText => onTextChange(updatedText, index),
    onOrderChange: (x, nextIndex) => changeOrder(x, nextIndex)
  }))))))))));
};
Picker.propTypes = {
  picker: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  onHasChange: PropTypes.func.isRequired,
  showDisabled: PropTypes.number.isRequired,
  lang: PropTypes.string.isRequired,
  hasChange: PropTypes.bool.isRequired,
  pickerActions: PropTypes.shape({
    serverIndex: PropTypes.func,
    serverSaveAll: PropTypes.func
  }).isRequired,
  location: PropTypes.func,
  canView: PropTypes.bool.isRequired,
  notificationActions: PropTypes.shape({}).isRequired,
  pickerType: PropTypes.shape({
    name: PropTypes.string,
    tableName: PropTypes.string
  }).isRequired
};
export { Picker as TestPicker };
export default entityComposition(Picker, ['picker', 'notification'], {
  canView: () => hasPermission('general', 'picker_edit')
});